import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MapPin, CalendarOff, Plus, ChevronDown, ChevronUp } from 'lucide-react';
import { toMinutes } from './scheduleUtils';

// The "Daily schedule" tools row that sits just above the unassigned strip
// (mirrors the prototype): summary chips + the review pill + a "More stats"
// popover on the left, the three management actions on the right.
const Chip = ({ children, tone = 'default' }) => {
  const tones = {
    default: 'bg-[#F2F6FB] text-[#586a82] dark:bg-[#31373f] dark:text-gray-300',
    alert: 'bg-rose-50 text-rose-600 dark:bg-rose-900/20 dark:text-rose-400',
    good: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-900/10 dark:text-emerald-400',
    purple: 'bg-purple-50 text-purple-600 dark:bg-purple-900/20 dark:text-purple-300',
  };
  return <span className={`inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-[10px] font-bold ${tones[tone]}`}>{children}</span>;
};

// "Xh Ym" past 59 min, else "Ym".
const fmtDur = (min) => {
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (h && m) return `${h}h ${m}m`;
  if (h) return `${h}h`;
  return `${m}m`;
};

// One row in the "More stats" popover.
const StatRow = ({ label, value, tone }) => (
  <div className="flex items-center justify-between rounded-xl bg-[#F7F9FC] dark:bg-[#31373f] px-3.5 py-2.5">
    <span className="text-[13px] font-semibold text-[#334155] dark:text-gray-200">{label}</span>
    <span className={`text-[14px] font-bold ${tone === 'good' ? 'text-emerald-600 dark:text-emerald-400' : 'text-[#1E293B] dark:text-gray-200'}`}>{value}</span>
  </div>
);

const ScheduleToolbar = ({
  summary = {},
  zones = [],
  unassigned = [],
  reviewCount = 0,
  onReview,
  onCreateZone,
  onAvailability,
  onTimeAway,
}) => {
  const [statsOpen, setStatsOpen] = useState(false);
  const statsRef = useRef(null);

  // Close the popover on outside click.
  useEffect(() => {
    if (!statsOpen) return undefined;
    const onDoc = (e) => { if (statsRef.current && !statsRef.current.contains(e.target)) setStatsOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [statsOpen]);

  // Derived counters for the headline chips + the "More stats" popover.
  const stats = useMemo(() => {
    let visits = (unassigned || []).length;
    let availabilityBlocks = 0;
    let shiftMinutes = 0;
    let visitMinutes = 0;
    (zones || []).forEach((z) => (z.carers || []).forEach((c) => {
      availabilityBlocks += (c.extra_availability || []).length;
      if (c.shift) {
        const s = toMinutes(c.shift.start);
        const e = toMinutes(c.shift.end);
        if (s != null && e != null && e > s) shiftMinutes += e - s;
      }
      (c.visits || []).forEach((v) => {
        visits += 1;
        const s = toMinutes(v.start);
        const e = toMinutes(v.end);
        if (s != null && e != null && e > s) visitMinutes += e - s;
      });
    }));
    return { visits, availabilityBlocks, freeCapacity: Math.max(0, shiftMinutes - visitMinutes) };
  }, [zones, unassigned]);

  return (
    <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-[#E2E8F0] dark:border-gray-800 bg-white dark:bg-[#262b33] px-4 py-3 shadow-sm">
      <span className="text-[13px] font-semibold text-[#334155] dark:text-gray-200">Daily schedule</span>

      <div className="flex flex-wrap items-center gap-1.5">
        <Chip><b className="text-[#23334b] dark:text-gray-100">{stats.visits}</b> visits</Chip>
        <Chip><b className="text-[#23334b] dark:text-gray-100">{summary.carers || 0}</b> carers</Chip>
        <Chip tone="good"><b>{summary.assigned_visits || 0}</b> assigned</Chip>
        {summary.unassigned_visits > 0 && <Chip tone="purple"><b>{summary.unassigned_visits}</b> unassigned</Chip>}

        {reviewCount > 0 && (
          <button
            onClick={onReview}
            className="inline-flex items-center gap-1.5 rounded-md bg-rose-50 dark:bg-rose-900/20 px-2.5 py-1 text-[10px] font-bold text-rose-600 dark:text-rose-400 hover:bg-rose-100 dark:hover:bg-rose-900/40 transition"
            title={`${reviewCount} item(s) need review`}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-rose-500" /> {reviewCount} to review
          </button>
        )}

        {/* More stats popover */}
        <div className="relative" ref={statsRef}>
          <button
            onClick={() => setStatsOpen((v) => !v)}
            className={`inline-flex items-center gap-1 rounded-md border px-2.5 py-1 text-[10px] font-bold transition ${
              statsOpen ? 'border-[#BCD0F7] dark:border-blue-900/50 bg-blue-50 dark:bg-blue-900/20 text-[#245FCF] dark:text-blue-400' : 'border-transparent bg-[#F2F6FB] text-[#586a82] hover:bg-[#E8EEF6] dark:bg-[#31373f] dark:text-gray-300 dark:hover:bg-[#3a414b]'
            }`}
          >
            More stats {statsOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>

          {statsOpen && (
            <div className="absolute left-0 top-full z-40 mt-2 w-72 rounded-2xl border border-[#E2E8F0] dark:border-gray-800 bg-white dark:bg-[#262b33] p-3 shadow-xl">
              <div className="mb-2 flex items-center justify-between px-1">
                <span className="text-[13px] font-bold text-[#1E293B] dark:text-gray-200">More stats</span>
                <span className="text-[11px] font-medium text-[#94A3B8] dark:text-gray-500">Supporting daily numbers</span>
              </div>
              <div className="flex flex-col gap-1.5">
                <StatRow label="Zones" value={summary.zones || 0} />
                <StatRow label="Availability blocks" value={stats.availabilityBlocks} />
                <StatRow label="Time away" value={summary.time_away || 0} />
                <StatRow label="Free capacity" value={fmtDur(stats.freeCapacity)} tone="good" />
                <StatRow label="Unassigned today" value={summary.unassigned_visits || 0} />
                <StatRow label="Unassigned this week" value={summary.unassigned_week || 0} />
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="ml-auto flex items-center gap-2 flex-wrap">
        <button
          onClick={onTimeAway}
          className="bg-white dark:bg-[#31373f] text-[#245FCF] dark:text-blue-400 border border-[#BCD0F7] dark:border-blue-900/50 px-3 py-1.5 rounded-lg font-semibold hover:bg-blue-50 dark:hover:bg-blue-900/20 transition flex items-center gap-1.5 text-[12px]"
        >
          <CalendarOff className="w-3.5 h-3.5" /> Time away
        </button>
        <button
          onClick={onAvailability}
          className="bg-white dark:bg-[#31373f] text-[#245FCF] dark:text-blue-400 border border-[#BCD0F7] dark:border-blue-900/50 px-3 py-1.5 rounded-lg font-semibold hover:bg-blue-50 dark:hover:bg-blue-900/20 transition flex items-center gap-1.5 text-[12px]"
        >
          <Plus className="w-3.5 h-3.5" /> Availability
        </button>
        <button
          onClick={onCreateZone}
          className="bg-white dark:bg-[#31373f] text-[#245FCF] dark:text-blue-400 border border-[#BCD0F7] dark:border-blue-900/50 px-3 py-1.5 rounded-lg font-semibold hover:bg-blue-50 dark:hover:bg-blue-900/20 transition flex items-center gap-1.5 text-[12px]"
        >
          <MapPin className="w-3.5 h-3.5" /> Create zone
        </button>
      </div>
    </div>
  );
};

export default ScheduleToolbar;
